import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { toast } from "react-toastify";
import { FILTER_DATE } from "../../../constants/filter-types";
import { REPORTS_TRANSACTION } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import TransactionColumn from "../../../models/ColumnDefinitions/TransactionColumn";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import BuildingSearch from "../../../models/search/BuildingSearch";
import CompanySearch from "../../../models/search/CompanySearch";
import ControllerSearch from "../../../models/search/ControllerSearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import DoorSearch from "../../../models/search/DoorSearch";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import ReaderSearch from "../../../models/search/ReaderSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import TransactionSearch from "../../../models/search/TransactionSearch";
import ZoneSearch from "../../../models/search/ZoneSearch";
import { TableColumn } from "../../../models/table/TableColumn";
import { getBuildingNamesWithPagination } from "../../../services/buildingService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getControllerNamesWithPagnation } from "../../../services/controllerService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getDoorNamesWithPagination } from "../../../services/doorService";
import { getEventTypeNamesWithPagination } from "../../../services/eventTypeService";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { getTransactionReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { showError } from "../../../services/utilities/ServiceError";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { IAppState } from "../../../store/types";
import { getTodayDate } from "../../../utilities/DateUtils";
import { appendFilter } from "../../../utilities/filters";
import { auditRunReport } from "../../../services/auditService";

interface Props {
    isTodayReportType: boolean;
    reportId: string;
}

const Transactions: FC<Props> = ({ isTodayReportType, reportId }) => {
    const { translate } = useLocale();
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [refreshReport, setRefreshReport] = useState(true);
    const user = useSelector((state: IAppState) => (state.userInfo));

    useEffect(() => {
        setLookup([
            {
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.eventType,
                values: [],
                dataMethod: getEventTypeNamesWithPagination,
                searchAccessor: EventTypeSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.company,
                values: [],
                dataMethod: getCompanyNamesWithPagination,
                searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.department,
                values: [],
                dataMethod: getDepartmentNamesWithPagination,
                searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.location,
                values: [],
                dataMethod: getDoorNamesWithPagination,
                searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.zone,
                values: [],
                dataMethod: getZoneNamesWithPagination,
                searchAccessor: ZoneSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.building,
                values: [],
                dataMethod: getBuildingNamesWithPagination,
                searchAccessor: BuildingSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.controller,
                values: [],
                dataMethod: getControllerNamesWithPagnation,
                searchAccessor: ControllerSearch.find(column => column.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.terminal,
                values: [],
                dataMethod: getReaderNamesWithPagination,
                searchAccessor: ReaderSearch.find(column => column.accessor === "name")?.searchOnName
            },
        ]);
    }, [])

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod)
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_TRANSACTION;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        // Filters that should always show for this view
        const staticFilterKeys = ["dateOnly"];

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }],
                }];
            }
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
    }
    // <-- end context

    const getReportColumn = (): TableColumn[] => {
        if (isTodayReportType) {
            return TransactionColumn.filter((column: TableColumn) => { return column.accessor !== 'dateOnly' });
        }
        return TransactionColumn;
    }

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getTransactionReport(user.profileId, translate, { ...listParameters, filters: listParameters?.filters });
        } catch (error) {
            return Promise.reject(error);
        }
    } 

    return (<TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={getReportColumn()}
        searchDescriptor={TransactionSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.transactions)}
            selectRows={false}
            showFilterMenu={isTodayReportType ? false : true}
            cyName="transactions">
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default Transactions;
